import { Trans, useLingui } from '@lingui/react/macro'
import {
  ActionIcon,
  Box,
  Button,
  Collapse,
  CopyButton,
  Group,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { AppModal } from '@markku/components'
import { COMPANY_INFO, INVOICE_PAYMENT_PERIOD_DAYS } from '@markku/types'
import {
  formatCompanyNameForPayment,
  formatDate,
  formatEuros,
  formatIban,
  generateEpcQrCodeData,
  generateVirtualBarcodeString,
} from '@markku/utils'
import {
  IconBarcode,
  IconCheck,
  IconCopy,
  IconQrcode,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { lazy, Suspense, useState } from 'react'

// Lazy load BwipCanvas component with bwip-js
const BwipCanvas = lazy(() =>
  import('./BwipCanvas').then((module) => ({ default: module.BwipCanvas })),
)

interface Invoice {
  referenceNumber: string
  totalAmount: string
  sentAt: string | null
  createdAt: string
}

interface WireTransferModalProps {
  opened: boolean
  onClose: () => void
  invoice: Invoice
}

type CodeType = 'qr' | 'barcode' | null

export function WireTransferModal({
  opened,
  onClose,
  invoice,
}: WireTransferModalProps) {
  const { t } = useLingui()
  const [visibleCode, setVisibleCode] = useState<CodeType>(null)

  const toggleCode = (code: 'qr' | 'barcode') => {
    setVisibleCode((current) => (current === code ? null : code))
  }

  const amount = parseFloat(invoice.totalAmount)
  const dueDate = dayjs(invoice.sentAt || invoice.createdAt)
    .add(INVOICE_PAYMENT_PERIOD_DAYS, 'days')
    .toDate()

  // Format company name for display (uppercase, dots to spaces)
  const formattedCompanyName = formatCompanyNameForPayment(COMPANY_INFO.name)

  // Note: recipientName not used in virtual barcode, but formatted internally by EPC QR code
  const barcodeData = {
    iban: COMPANY_INFO.iban,
    bic: COMPANY_INFO.bic,
    amount,
    reference: invoice.referenceNumber,
    dueDate,
    recipientName: COMPANY_INFO.name,
  }

  const virtualBarcode = generateVirtualBarcodeString(barcodeData)
  const qrCodeData = generateEpcQrCodeData(barcodeData)

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={t`Wire transfer instructions`}
      size="md"
      cancelProps={{ label: t`Close` }}
    >
      <Stack gap="md">
        {/* Payment details */}
        <Stack gap="xs">
          <PaymentDetailRow
            label={<Trans>Recipient</Trans>}
            value={formattedCompanyName}
          />
          <PaymentDetailRow
            label={<Trans>IBAN</Trans>}
            value={formatIban(COMPANY_INFO.iban)}
            copyValue={COMPANY_INFO.iban.replace(/\s/g, '')}
          />
          <PaymentDetailRow
            label={<Trans>Reference</Trans>}
            value={invoice.referenceNumber}
            copyValue={invoice.referenceNumber}
          />
          <PaymentDetailRow
            label={<Trans>Amount</Trans>}
            value={formatEuros(invoice.totalAmount)}
          />
          <PaymentDetailRow
            label={<Trans>Due date</Trans>}
            value={formatDate(dueDate.toISOString())}
          />
        </Stack>

        {/* Show code buttons */}
        <Group gap="xs">
          <Button
            variant={visibleCode === 'qr' ? 'filled' : 'light'}
            size="xs"
            leftSection={<IconQrcode size={14} />}
            onClick={() => toggleCode('qr')}
          >
            <Trans>QR code</Trans>
          </Button>
          <Button
            variant={visibleCode === 'barcode' ? 'filled' : 'light'}
            size="xs"
            leftSection={<IconBarcode size={14} />}
            onClick={() => toggleCode('barcode')}
          >
            <Trans>Barcode</Trans>
          </Button>
        </Group>

        {/* QR Code */}
        <Collapse in={visibleCode === 'qr'}>
          <Box ta="center" py="md">
            <Suspense
              fallback={<Skeleton height={200} width={200} mx="auto" />}
            >
              <BwipCanvas type="qrcode" data={qrCodeData} />
            </Suspense>
            <Text size="xs" c="dimmed" mt="xs">
              <Trans>Scan with your banking app</Trans>
            </Text>
          </Box>
        </Collapse>

        {/* Code 128 Barcode */}
        <Collapse in={visibleCode === 'barcode'}>
          <Box ta="center" py="md" style={{ overflowX: 'auto' }}>
            <Suspense fallback={<Skeleton height={60} width={300} mx="auto" />}>
              <BwipCanvas type="code128" data={virtualBarcode} />
            </Suspense>
            <Text size="xs" c="dimmed" mt="xs">
              <Trans>Scan with your banking app</Trans>
            </Text>
          </Box>
        </Collapse>

        {/* Virtual barcode string - always visible */}
        <Stack gap={4}>
          <Text size="xs" c="dimmed" fw={500}>
            <Trans>Virtual barcode</Trans>
          </Text>
          <Group gap="xs" align="center">
            <Text
              size="xs"
              c="dimmed"
              style={{ fontFamily: 'monospace', flex: 1 }}
            >
              {virtualBarcode}
            </Text>
            <CopyButton value={virtualBarcode}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? t`Copied` : t`Copy`}>
                  <ActionIcon
                    variant="light"
                    color={copied ? 'green' : 'gray'}
                    size="sm"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </Stack>
      </Stack>
    </AppModal>
  )
}

function PaymentDetailRow({
  label,
  value,
  copyValue,
}: {
  label: React.ReactNode
  value: string
  copyValue?: string
}) {
  const { t } = useLingui()

  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" fw={500}>
          {value}
        </Text>
        {copyValue && (
          <CopyButton value={copyValue}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? t`Copied` : t`Copy`}>
                <ActionIcon
                  variant="subtle"
                  color={copied ? 'green' : 'gray'}
                  size="xs"
                  onClick={copy}
                >
                  {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        )}
      </Group>
    </Group>
  )
}
